import { getShops } from '@/services/stores';
import {
  initializeDurations,
  initializeMinimumAmounts,
  initializeRewardPercentages,
  initializeTypes,
} from '@/utils/filters';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { ModelMerchantShopDirectoryShopV2 } from '@layup/api-sdk';
import { useThemeStore } from './theme';
import { addBreadcrumb, createWarning } from '@/utils/monitoring.utils';

export const useShopsStore = defineStore('shops', () => {
  const themeStore = useThemeStore();

  const allShops = ref<ModelMerchantShopDirectoryShopV2[]>([]);
  const categories = ref<string[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchedAt = ref<number | null>(null);

  const FIFTEEN_MINUTES = 15 * 60 * 1000;

  // Filter state
  const filterCategories = ref<{ name: string; selected: boolean }[]>([]);
  const filterTypes = ref(initializeTypes());
  const filterDurations = ref(initializeDurations());
  const filterRewardPercentages = ref(initializeRewardPercentages());
  const filterMinimumAmounts = ref(initializeMinimumAmounts());
  const sortBy = ref<'a-z' | 'z-a' | 'newest' | 'default'>('default');

  const PAYMENT_PLAN_MERCHANTS = ['TRAVELSTART', 'DREAM RESORTS', 'HOWLER', 'LB FUNCTION'];

  // Watch categories from API and initialize filter categories
  watch(categories, (newCategories) => {
    if (newCategories && newCategories.length > 0 && filterCategories.value.length === 0) {
      filterCategories.value = newCategories.map((cat) => ({ name: cat, selected: false }));
    }
  });

  // Re-initialize filters when product changes
  watch(
    () => themeStore.current,
    () => {
      filterTypes.value = initializeTypes();
      filterDurations.value = initializeDurations();
      filterRewardPercentages.value = initializeRewardPercentages();
      filterMinimumAmounts.value = initializeMinimumAmounts();
    },
  );

  // Computed properties for selected values (for filtering logic)
  const selectedCategories = computed(() =>
    filterCategories.value.filter((cat) => cat.selected).map((cat) => cat.name),
  );

  const selectedTypes = computed(() => filterTypes.value.filter((type) => type.selected).map((type) => type.name));

  const selectedDurations = computed(() => filterDurations.value.filter((dur) => dur.selected).map((dur) => dur.value));

  const selectedRewardPercentages = computed(() =>
    filterRewardPercentages.value.filter((perc) => perc.selected).map((perc) => perc.name),
  );

  const selectedMinimumAmounts = computed(() =>
    filterMinimumAmounts.value.filter((amt) => amt.selected).map((amt) => amt.name),
  );

  // Computed filtered shops based on product and filters
  const shops = computed(() => {
    let filtered = [...allShops.value];

    // Filter by product type
    if (themeStore.current === 'boost') {
      filtered = filtered.filter((shop) => shop.boostRewards?.enabled === true);
    }
    else if (themeStore.current === 'plans') {
      filtered = filtered.filter((shop) => !shop.boostRewards?.enabled && PAYMENT_PLAN_MERCHANTS.includes(shop?.name?.toUpperCase() ?? ''));
    }
    else {
      filtered = filtered.filter((shop) => !shop.boostRewards?.enabled  && !PAYMENT_PLAN_MERCHANTS.includes(shop?.name?.toUpperCase() ?? ''));
    }

    // Apply category filters
    if (selectedCategories.value.length > 0) {
      filtered = filtered.filter((shop) => shop.categories?.some((cat) => selectedCategories.value.includes(cat)));
    }

    // Apply type filters (online/in-store)
    if (selectedTypes.value.length > 0) {
      filtered = filtered.filter((shop) => {
        if (!shop.availability) return false;

        const availability = shop.availability.toLowerCase();
        const matchesOnline = selectedTypes.value.includes('Online') && availability.includes('online');
        const matchesInStore = selectedTypes.value.includes('In-store') && availability.includes('in-store');

        return matchesOnline || matchesInStore;
      });
    }

    // Apply duration filters (layby only)
    if (themeStore.current === 'layby' && selectedDurations.value.length > 0) {
      filtered = filtered.filter((shop) => {
        const paymentPlan = shop.paymentPlan;
        if (!paymentPlan?.enabled || paymentPlan.maxMonths == null || paymentPlan.minMonths == null) {
          return false;
        }

        return selectedDurations.value.some((selectedDuration) => {
          if (selectedDuration === '12+') {
            return paymentPlan.maxMonths! > 12;
          } else {
            const duration = selectedDuration as number;
            return paymentPlan.maxMonths! >= duration;
          }
        });
      });
    }

    // Apply reward percentage filters (boost only)
    if (themeStore.current === 'boost' && selectedRewardPercentages.value.length > 0) {
      filtered = filtered.filter((shop) => {
        const reward = shop.boostRewards?.rewardPercentage;
        if (reward == null) return false;

        return selectedRewardPercentages.value.some((selected) => {
          if (selected === '0-10%') return reward >= 0 && reward <= 10;
          if (selected === '10-20%') return reward > 10 && reward <= 20;
          if (selected === '20-30%') return reward > 20 && reward <= 30;
          if (selected === '30%+') return reward > 30;

          return false;
        });
      });
    }

    // Apply minimum amount filters (boost only)
    if (themeStore.current === 'boost' && selectedMinimumAmounts.value.length > 0) {
      filtered = filtered.filter((shop) => {
        const minAmount = shop.boostRewards?.minSavingsAmount;

        if (minAmount == null) return false;

        return selectedMinimumAmounts.value.some((selected) => {
          if (selected === '< R1000') return minAmount < 100000;
          if (selected === 'R1000') return minAmount >= 100000;
          if (selected === 'R2000') return minAmount >= 200000;
          if (selected === 'R3000') return minAmount >= 300000;
          if (selected === 'R4000') return minAmount >= 400000;
          if (selected === 'R5000+') return minAmount >= 500000;

          return false;
        });
      });
    }

    // Apply sorting
    if (sortBy.value === 'a-z') {
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy.value === 'z-a') {
      filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy.value === 'newest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Newest first
      });
    }

    return filtered;
  });

  const featuredShops = computed(() => {
    if (themeStore.current === 'boost') {
      return allShops.value.filter((shop) => shop.boostRewards?.enabled === true && shop.featured === true);
    }
    else if (themeStore.current === 'plans') {
      return allShops.value.filter((shop) => !shop.boostRewards?.enabled && shop.featured === true && PAYMENT_PLAN_MERCHANTS.includes(shop?.name?.toUpperCase() ?? ''));
    }
    return allShops.value.filter((shop) => !shop.boostRewards?.enabled && shop.featured === true);
  });

  const fetchStores = async (force = false) => {
    const now = Date.now();
    if (!force && lastFetchedAt.value && now - lastFetchedAt.value < FIFTEEN_MINUTES) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await getShops();
      allShops.value = response.shops ?? [];
      categories.value = response.categories ?? [];
      lastFetchedAt.value = Date.now();
    } catch (err) {
      error.value = 'Failed to load store directory.';
      addBreadcrumb('shop.fetch', 'Failed to fetch shops from API');
      createWarning(err);
    } finally {
      loading.value = false;
    }
  };

  const clearFilters = () => {
    filterCategories.value.forEach((cat) => (cat.selected = false));
    filterTypes.value.forEach((type) => (type.selected = false));
    filterDurations.value.forEach((dur) => (dur.selected = false));
    filterRewardPercentages.value.forEach((perc) => (perc.selected = false));
    filterMinimumAmounts.value.forEach((amt) => (amt.selected = false));
  };

  const setSortBy = (sort: 'a-z' | 'z-a' | 'newest' | 'default') => {
    sortBy.value = sort;
  };

  return {
    // Filtered/computed
    shops,
    featuredShops,

    // Metadata
    categories,
    loading,
    error,
    lastFetchedAt,

    // Filter state
    filterCategories,
    filterTypes,
    filterDurations,
    filterRewardPercentages,
    filterMinimumAmounts,
    sortBy,
    setSortBy,

    // Computed selected values
    selectedCategories,
    selectedTypes,
    selectedDurations,
    selectedRewardPercentages,
    selectedMinimumAmounts,

    // Actions
    fetchStores,
    clearFilters,
  };
});
